import Checkbox from "@rio-cloud/rio-uikit/Checkbox";
import Multiselect from "@rio-cloud/rio-uikit/Multiselect";
import Switch from "@rio-cloud/rio-uikit/Switch";
import Select from "@rio-cloud/rio-uikit/Select";
import { Spinner } from "@rio-cloud/rio-uikit";
import { isMobile } from "@rio-cloud/rio-uikit/utils/deviceUtils";
import { useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";

import { useSelector } from "react-redux";
import { getUserProfile } from "../../../../../configuration/login/loginSlice";

import { Driver } from "../../../../../models/driver";
import { CheckupService } from "../../../../../models/checkup";

import { formatPhoneNumber } from "../../../../../utils/phoneUtils";

import { IAssetAndDriverForm, useSchedulingFormAsset } from "../../../../../hooks/useSchedulingForm";
import { AssetModel } from "../../../../../models/asset";
import useAssetModels from "../../../../../hooks/useAssetModels";
import SearchAutocomplete from "../../../../../components/form/searchAutocomplete";
import { useDebouncedCallback } from "../../../../../hooks/useDebounce";
import VehicleIcon from "../../../../../components/VehicleIcon";

interface IAssetsAndDriversProps {
  setStep: (step: number) => void;
  drivers: Driver[];
  form?: IAssetAndDriverForm;
  setAppointmentForm: (form: IAssetAndDriverForm) => void;
}

const AssetsAndDriversForm = ({
  setStep,
  drivers,
  form,
  setAppointmentForm,
}: IAssetsAndDriversProps) => {
  const navigate = useNavigate();
  const intl = useIntl();
  const t = (id: string) => intl.formatMessage({ id: `intl-msg:appointment.step1.${id}` });
  const userProfile = useSelector(getUserProfile);
  const {
    loading,
    vehicleLoading,
    vehicleError,
    disableAssetGroup,
    showComments, setShowComments,
    setCheckup,
    aditionalServices,
    setCheckupByGroupId,
    formIsValid,
    checkIfUserIsDriver, chooseDriver
  } = useSchedulingFormAsset(setAppointmentForm, form);

  const { assetModels } = useAssetModels();

  // Callbacks debounced para odômetro e horímetro (3000ms de delay)
  const debouncedOdometerCheckup = useDebouncedCallback(
    async (value: number, asset: any) => {
      if (value && asset) {
        await setCheckup(value, "odometer", asset);
      }
    },
    3000
  );

  const debouncedHourmeterCheckup = useDebouncedCallback(
    async (value: number, asset: any) => {
      if (value && asset) {
        await setCheckup(value, "hourmeter", asset);
      }
    },
    3000
  );

  return (
    <>
      <div className="scheduling-page p-10 h-[70vh] overflow-auto">
        <div className="scheduling-page__header">
          <h2 className="text-size-h2 text-color-black font-semibold text-uppercase">
            1. {t('title')}
          </h2>
          <p className="text-color-status-resting text-size-16 padding-top-10">
            {t('subtitle')}
          </p>
        </div>

        <div className="border rounded padding-15 margin-top-20 scheduling-page__fieldset">
          <h6 className="text-bold text-size-16">{t('vehicleInfo')}</h6>
          {form?.asset && (
            <div className="border rounded padding-15 margin-top-20 scheduling-page__fieldset">
              <div className="flex items-center gap-4">
                <VehicleIcon 
                  chassis={form?.asset.chassis} 
                  size={80}
                />

                <div className="flex-1 padding-left-10 truncate">
                  <p className="text-color-black text-size-16 font-medium m-0">
                    {form?.asset.name}
                  </p>
                  <p className="text-color-status-resting text-size-14 m-0 truncate">
                    {t('chassisLabel')} - {form?.asset.chassis}
                  </p>
                </div>

                {(form?.asset?.plans?.length ?? 0) > 0 && (
                  <div className="flex gap-6 margin-bottom-20">
                    <span className="px-2 py-1 green-tag">RIO</span>
                    <span className="px-2 py-1 green-tag">{t('inWarranty')}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className={`flex justify-between w-full gap-6 ${isMobile() ? "flex-wrap" : "mt-5"} ${isMobile() && form?.asset ? "mt-5" : ""}`}>
            <div className={`form-group form-group-lg ${isMobile() ? "w-full" : "w-1/3"}`}>
              <label htmlFor="placa">{t('licensePlate')}</label>
              <input
                id="placa"
                className="form-control"
                type="text"
                value={form?.asset?.licensePlate || ""}
                onChange={(e) =>
                setAppointmentForm({
                  ...form,
                  asset: {
                    ...form!.asset!,
                    licensePlate: e.target.value,
                    },
                  })
                }
                disabled={!!form?.asset?.licensePlateFromEP}
                maxLength={7}
              />
            </div>

            <div className={`form-group form-group-lg ${isMobile() ? "w-full" : "w-1/3"}`}>
              <label htmlFor="emissionStandard">{t('legislation')}</label>
              {form?.asset?.emissionStandard ? (
                <input
                  id="emissionStandard"
                  className="form-control"
                  type="text"
                  value={form?.asset?.emissionStandard}
                  disabled
                />
              ) : (
                <Select
                  id="emissionStandard"
                  className="w-full emission-select select"
                  placeholder={t('selectLegislation')}
                  options={[
                    { id: "EURO 3", label: "EURO 3" },
                    { id: "EURO 5", label: "EURO 5" },
                    { id: "EURO 6", label: "EURO 6" },
                    { id: "ELETRICO", label: "ELÉTRICO" },
                  ]}
                  value={form?.asset?.emissionStandard ? [form?.asset?.emissionStandard] : []}
                  onChange={(option: any) => {
                    setAppointmentForm({
                      ...form,
                      asset: form?.asset ? { ...form.asset, emissionStandard: option?.id } : undefined,
                    });
                  }}
                  useFilter
                  noItemMessage={intl.formatMessage({ id: "intl-msg:appointment.step1.noGroupFound" })}
                />
              )}
            </div>

            <div className={`form-group form-group-lg ${isMobile() ? "w-full" : "w-1/3"}`}>
              <label htmlFor="model">{t('vehicleModel')}</label>
              {form?.asset?.model && form?.asset?.model !== "-" ? (
                <input
                  id="model"
                  className="form-control"
                  type="text"
                  value={form?.asset?.model}
                  disabled
                />
              ) : (
                <SearchAutocomplete
                  id="model"
                  placeholder={t('vehicleModel')}
                  fetchOptions={async (searchTerm) => assetModels
                    .filter((model) => model.modelId && model.description && model.description.toLowerCase().includes(searchTerm?.toLowerCase()))
                  }
                  optionLabel={{
                    modelCode: t('code'),
                    description: t('model'),
                  }}
                  optionRenderer={(model: AssetModel) => (
                    <div>
                      <strong>{t('code')}:</strong> {model.modelCode} - <strong>{t('model')}:</strong> {model.description ?? "-"}
                    </div>
                  )}
                  onSelectedOption={(model: AssetModel) => {
                    setAppointmentForm({
                      ...form,
                      asset: form?.asset ? { ...form?.asset, model: model.description, modelCode: model.modelCode, vehicleModel: model.modelId } : undefined,
                    });
                  }}
                  overlay
                  overlayClassName="!w-[44%]"
                  defaultValue={form?.asset?.model && assetModels.find((model) => model.modelId === form?.asset?.model)}
                  disabled={disableAssetGroup}
                />
              )}
            </div>

            <div className={`form-group form-group-lg ${isMobile() ? "w-full" : "w-1/3"}`}>
              <label htmlFor="group">{t('maintenanceGroup')}*</label>
              <Select
                id="group"
                placeholder={t('searchMaintenanceGroup')}
                className="select"
                options={[
                  { id: '1', label: t('groupRoad') },
                  { id: '2', label: t('groupMixed') },
                  { id: '3', label: t('groupSevere') },
                  { id: '4', label: t('groupSpecial') },
                ].map((group: any) => ({ id: group.id, label: group.label }))}
                value={form?.asset?.group ? [form?.asset?.group] : []}
                useFilter
                noItemMessage={t('noGroupFound')}
                onChange={(e) => setCheckupByGroupId(e?.id)}
                disabled={disableAssetGroup}
              />
              {!disableAssetGroup && <div className="text-size-12 text-color-status-resting padding-top-10 padding-left-10">
                * {t('maintenanceGroupDisclaimer')}
              </div>}
            </div>
          </div>

          <div className="flex justify-between w-full gap-6">
            <div className="form-group form-group-lg w-1/2">
              <label htmlFor="odometer">{t('odometer')}*</label>
              <div className="input-group">
                <input
                  id="odometer"
                  className="form-control"
                  type="text"
                  value={form?.asset?.odometer}
                  disabled={loading}
                  onChange={(e) => {
                    const value = Number(e.target.value);
                    setAppointmentForm({
                      ...form,
                      asset: form?.asset
                        ? {
                          ...form?.asset,
                          odometer: value,
                          group: form?.asset?.group ?? "",
                        }
                        : undefined,
                    });
                    // Executa o cálculo com debounce
                    debouncedOdometerCheckup(value, form?.asset);
                  }}
                  onBlur={async (e) => {
                    if (e.target.value) {
                      await setCheckup(Number(e.target.value), 'odometer', form?.asset);
                    }
                  }}
                />
              </div>
            </div>

            <div className="form-group form-group-lg w-1/2">
              <label htmlFor="hourmeter">{t('hourmeter')}*</label>
              <div className="input-group">
                <input
                  id="hourmeter"
                  className="form-control no-controls"
                  type="number"
                  value={form?.asset?.hourmeter}
                  disabled={loading}
                  onChange={(e) => {
                    const value = Number(e.target.value);
                    setAppointmentForm({
                      ...form,
                      asset: form?.asset
                        ? {
                          ...form?.asset,
                          hourmeter: value,
                          group: form?.asset?.group ?? "",
                        }
                        : undefined,
                    });
                    // Executa o cálculo com debounce
                    debouncedHourmeterCheckup(value, form?.asset);
                  }}
                  onBlur={async (e) => {
                    if (e.target.value) {
                      await setCheckup(Number(e.target.value), 'hourmeter', form?.asset);
                    }
                  }}
                />
              </div>
            </div>
          </div>

          <div className="flex flex-column">
            {checkIfUserIsDriver(userProfile, drivers) && (
              <Checkbox
                id="checkbox-motorista"
                checked={form?.scheduleByDriver}
                onChange={() => {
                  const driverId = userProfile?.sub?.split(":")[1];
                  const driver = drivers.find(
                    (driver) => driver.id === driverId
                  );
                  setAppointmentForm({
                    ...form,
                    scheduleByDriver: !form?.scheduleByDriver,
                    driver: !form?.scheduleByDriver ? driver : form?.driver,
                  });
                }}
              >
                {t('iAmTheDriver')}
              </Checkbox>
            )}
            <div className={`flex justify-between w-full gap-6 ${isMobile() ? "flex-wrap" : "mt-5"}`}>
              <div className={`form-group form-group-lg ${isMobile() ? "w-full" : "w-1/2"}`}>
                <label htmlFor="motorista">{t('driverName')}</label>
                <Select
                  placeholder={t('driverNamePlaceholder')}
                  className="select"
                  options={drivers.map((driver: Driver) => ({
                    id: driver.id ?? '',
                    label: driver.name ?? '',
                  }))}
                  value={form?.driver ? [form?.driver.id ?? ''] : []}
                  useFilter
                  noItemMessage={t('noDriverFound')}
                  onChange={(e) => chooseDriver(e?.id ?? "", drivers)}
                  inputAddon="rioglyph rioglyph-search"
                />
              </div>

              <div className={`form-group form-group-lg ${isMobile() ? "w-full" : "w-1/2"}`}>
                <label htmlFor="cnh">{t('driverLicense')}</label>
                <div className="input-group">
                  <input
                    id="cnh"
                    className="form-control"
                    type="text"
                    placeholder={t('driverLicense')}
                    maxLength={12}
                    value={form?.driver?.license}
                    onChange={(e) =>
                      setAppointmentForm({
                        ...form,
                        driver: form?.driver
                          ? {
                            ...form?.driver,
                            license: e.target.value,
                          }
                          : undefined,
                      })
                    }
                  />
                </div>
              </div>
            </div>

            {/* Mensagem de erro para veículos com agendamento existente */}
            {vehicleError && (
              <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-md">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-6 w-6 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-red-700" style={{ fontSize: '16px' }}>
                      {vehicleError}
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className={`flex justify-between w-full gap-6 ${isMobile() ? "flex-wrap" : ""}`}>
              <div className={`form-group form-group-lg ${isMobile() ? "w-full" : "w-1/2"}`}>
                <label htmlFor="emailMotorista">{t('driverEmail')}</label>
                <div className="input-group">
                  <span className="input-group-addon">
                    <span className="rioglyph rioglyph-envelope text-color-black"></span>
                  </span>
                  <input
                    id="emailMotorista"
                    className="form-control"
                    type="text"
                    placeholder={t('driverEmail')}
                    value={form?.driver?.email}
                    onChange={(e) =>
                      setAppointmentForm({
                        ...form,
                        driver: form?.driver
                          ? {
                            ...form?.driver,
                            email: e.target.value,
                          }
                          : undefined,
                      })
                    }
                  />
                </div>
              </div>

              <div className={`form-group form-group-lg ${isMobile() ? "w-full" : "w-1/2"}`}>
                <label htmlFor="telefoneMotorista">{t('driverPhone')}</label>
                <div className="input-group">
                  <span className="input-group-addon">
                    <span className="rioglyph rioglyph-earphone text-color-black"></span>
                  </span>
                  <input
                    id="telefoneMotorista"
                    className="form-control"
                    type="text"
                    placeholder={t('driverPhone')}
                    value={form?.driver?.phone}
                    onChange={(e) => {
                      const formattedPhoneNumber = formatPhoneNumber(
                        e.target.value
                      );
                      setAppointmentForm({
                        ...form,
                        driver: form?.driver
                          ? {
                            ...form?.driver,
                            phone: formattedPhoneNumber,
                          }
                          : undefined,
                      });
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="border rounded padding-15 margin-top-15 scheduling-page__fieldset">
          <h6 className="text-bold text-size-16">
            {t('reviewsSection')}
          </h6>
          {form?.asset?.model && form?.asset?.model !== "-" && form?.asset?.group && form?.asset?.group !== "-" ? (
            <>
              <div className="info-group">
                <p className="text-color-status-resting text-size-16 padding-top-10 padding-bottom-5">
                  {t('availableReviews')}*
                </p>

                <div className="display-flex gap-10">
                  {[
                    { id: "preventive", label: t('preventive') },
                    { id: "corrective", label: t('corrective') },
                    // { id: "workshopActions", label: "Campanhas de Campo" },
                  ].map((option) => {
                    if (option.id === "preventive" && (!form?.checkup || form?.checkup?.parts?.length === 0)) return null;
                    // if (
                    //   option.id === "workshopActions" &&
                    //   !form?.asset?.campaigns?.list?.length
                    // )
                    //   return null;
                    return (
                      <div className="avalilable-revisions border rounded">
                        <Checkbox
                          onChange={() => {
                            let schedulingTypes = form?.schedulingTypes;

                            schedulingTypes = schedulingTypes?.includes(option.id) ? schedulingTypes?.filter(type => type !== option.id) : [...(schedulingTypes ?? []), option.id];

                            setAppointmentForm({
                              ...form,
                              schedulingTypes,
                            });
                          }}
                          checked={form?.schedulingTypes?.includes(option.id)}
                          custom
                          className="padding-y-5 padding-x-10"
                        >
                          <div className="checkbox-text-wrapper display-flex gap-10">
                            <div className="checkbox-text" />
                            <div className="flex-1-1">{option.label}</div>
                          </div>
                        </Checkbox>
                      </div>
                    );
                  })}
                </div>
              </div>

              {form?.asset?.chassis !== '-' && form?.schedulingTypes?.length ? (
                <div
                  style={{
                    backgroundColor: "#ECFDF3",
                    color: "#067647",
                    borderColor: "#ABEFC6",
                  }}
                  className="border rounded padding-15 margin-top-20"
                >
                  {form?.schedulingTypes?.includes("preventive") &&
                    form?.checkup && (
                      <div className="margin-bottom-20">
                        <div className="text-bold">
                          {form?.checkup?.description}
                        </div>
                        {(form?.checkup?.services ?? []).map(
                          (service: CheckupService) => (
                            <div className="padding-left-10 padding-top-10">
                              {service.name}
                            </div>
                          )
                        )}
                        {form?.checkup?.parts?.length ?
                          <>
                            <div className="text-bold padding-top-10">
                              {t('partsList')}
                            </div>
                            <div className="display-grid grid-cols-2 gap-x-20 gap-y-5 padding-top-10 padding-left-10">
                              {(form?.checkup?.parts ?? []).map((part) => (
                                <div key={part.code} className="display-flex flex-column gap-5">
                                  <div className="display-flex align-items-center justify-content-between gap-10">
                                    <span className="flex-1 min-w-0">{part.description} - {part.qtd}x</span>
                                    {/*
                                    // TODO: Implementar cobertura de peças
                                    <span className={`flex-shrink-0 px-2 py-1 rounded text-size-12 font-medium uppercase whitespace-nowrap text-white ${part.isCovered !== false ? "green-tag" : "bg-red-600"}`} title={part.isCovered === false && part.notCoveredReason ? part.notCoveredReason : undefined}>
                                      {part.isCovered !== false ? t('coveredByPlan') : t('noCoverage')}
                                    </span>
                                    */}
                                  </div>
                                  {/*
                                  // TODO: Implementar cobertura de peças
                                  part.isCovered === false && part.notCoveredReason?.trim() && (
                                    <div className="text-size-12 text-color-status-resting padding-left-10">
                                      {part.notCoveredReason}
                                    </div>
                                  )}
                                  */}
                                </div>
                              ))}
                            </div>
                          </>
                          : null}
                      </div>
                    )}

                  {/* {form?.schedulingTypes?.includes("workshopActions") && (
                <>
                  <div className="text-bold">Campanhas de campo</div>
                  {(form?.asset?.campaigns?.list ?? []).map(
                    (campaign: CampaignsDetails) => (
                      <div className="padding-left-10 padding-top-10">
                        {campaign.name}
                      </div>
                    )
                  )}
                </>
              )} */}
                </div>
              ) : null}
            </>) : <div className="text-color-status-resting text-size-16 padding-top-10 padding-bottom-5">
            {t('selectModelAndGroup')}
          </div>}
        </div>

        <div className="border rounded padding-15 margin-top-20 scheduling-page__fieldset">
          <div className={`flex justify-between ${isMobile() ? "flex-wrap" : ""}`}>
            <h6 className="text-bold text-size-16">{t('serviceHiring')}</h6>
            <div className={`flex items-center padding-left-10 padding-right-10 total-value ${isMobile() ? "w-full mt-5" : ""}`}>
              <span className="font--bold text-size-14 font-bold total-value__label">
                {t('total')}
              </span>
              <span className="font-bold text-size-14 padding-left-5 padding-right-5 margin-left-10 total-value__value">
                {(form?.aditionalServices ?? [])
                  .reduce((acc, service) => acc + service.price, 0)
                  .toLocaleString("pt-BR", {
                    style: "currency",
                    currency: "BRL",
                  })}
              </span>
            </div>
          </div>

          <div className="info-group">
            <p className="text-color-status-resting text-size-16 padding-top-10 padding-bottom-5">
              {t('addOtherServices')}
            </p>

            <div className="add-services">
              <Multiselect
                placeholder={t('selectServices')}
                options={aditionalServices.map((service) => ({
                  id: service.id,
                  label: service.name,
                  price: service.price,
                }))}
                className="select-icon"
                onChange={(response: any) => {
                  setAppointmentForm({
                    ...form,
                    aditionalServices: aditionalServices.filter(
                      (aditionalService) =>
                        response.includes(aditionalService.id)
                    ),
                  });
                }}
                value={form?.aditionalServices?.map((service) => service.id)}
                noItemMessage={t('noAdditionalService')}
              />
              <div className="services-cost">
                <span className="font-bold text-size-14 padding-left-5 padding-right-5 margin-left-10 total-value__value">
                  {(form?.aditionalServices ?? [])
                    .reduce((acc, service) => acc + service.price, 0)
                    .toLocaleString("pt-BR", {
                      style: "currency",
                      currency: "BRL",
                    })}
                </span>
              </div>
              <div className="show-comments margin-top-15 flex justify-end show-comments">
                <Switch
                  onChange={() => {
                    setShowComments(!showComments);
                    setAppointmentForm({
                      ...form,
                      comments: null,
                    });
                  }}
                  checked={showComments || !!form?.comments}
                  labelPosition="right"
                  color="secondary"
                >
                  <span className="text-size-14 text-color-status-resting">
                    {t('comment')}
                  </span>
                </Switch>
              </div>
              {(showComments || !!form?.comments) && (
                <div className="text-comments margin-top-20">
                  <textarea
                    className="form-control"
                    placeholder={t('commentPlaceholder')}
                    value={form?.comments ?? undefined}
                    onChange={(e) =>
                      setAppointmentForm({
                        ...form,
                        comments: e.target.value,
                      })
                    }
                  />
                </div>
              )}
            </div>
          </div>

          {/* <div className="info-group">
                            <p className="text-color-status-resting text-size-16 padding-top-10 padding-bottom-5">
                            Cesta de peças
                            </p>

                            <div className="add-services cart-row">
                            <Multiselect
                                placeholder="Compre peças para este agendamento"
                                options={services.map((service) => ({
                                id: service.id,
                                label: service.label,
                                value: service.value,
                                }))}
                                className="select-icon"
                                onChange={(response: any) =>
                                Notification.info(JSON.stringify(response))
                                }
                            />
                            <div className="services-cost">
                                <span className="font-bold text-size-14 padding-left-5 padding-right-5 margin-left-10 total-value__value">
                                R$ 0,00
                                </span>
                            </div>
                            </div>
                        </div> */}
        </div>

        <div className="border rounded padding-15 margin-top-20">
          <div className="flex justify-between">
            <h6 className="text-bold text-size-16">{t('generalObservations')}</h6>
          </div>

          <div className="info-group">
            <div className="add-services">
              <div className="text-comments margin-top-10">
                <textarea
                  className="form-control"
                  placeholder={t('vehicleIssuesPlaceholder')}
                  value={form?.obs}
                  onChange={(e) =>
                    setAppointmentForm({
                      ...form,
                      obs: e.target.value,
                    })
                  }
                ></textarea>
              </div>
              {form?.schedulingTypes?.includes("corrective") && (form?.obs?.trim() ?? "").length > 0 && (
                <div className="margin-top-15 padding-15 rounded bg-green-100 border border-green-200 text-color-darker text-size-14">
                  {t('coverageCheck')}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="page-info flex justify-between padding-15">
        <span className="text-color-status-resting font-size-14">
          {t('stepLabel')} <strong className="text-color-danger">1</strong> {t('ofLabel')} 4
        </span>
        <span className="text-color-status-resting font-size-14">
          {t('stepInfo')}
        </span>
      </div>
      <div className="flex justify-between navigation-buttons step-1">
        <button
          className="px-4 py-2 rounded-lg font-semibold button-color"
          onClick={() => navigate("/fleet-manager/preventive-maintenance")}
        >
          <span className="rioglyph rioglyph-arrow-left text-size-16"></span>{" "}
          {t('back')}
        </button>
        <button
          disabled={!formIsValid()}
          onClick={() => setStep(2)}
          className="px-4 py-2 btn btn-primary"
        >
          {t('next')}
        </button>
      </div>

      <Spinner show={loading || vehicleLoading} text={loading ? t('loading') : vehicleLoading ? t('loadingVehicle') : ''} isFullSized />
    </>
  );
};

export default AssetsAndDriversForm;
